/**
 * L'identité de la PERSONNE MORALE — la partie au contrat, pas la marque.
 *
 * Une société et un produit sont deux choses différentes, même quand ils portent le même
 * nom. `NOM_PRODUIT` (constants.ts) désigne ce qui est vendu ; `SOCIETE` désigne qui le
 * vend et qui signe. Aujourd'hui les deux disent « TerObs » — ils peuvent diverger
 * demain (une société qui édite deux produits, un produit racheté), et une page légale
 * qui les confond devient fausse ce jour-là sans que rien ne le signale.
 *
 * Pendant côté back : `backend/app/company.py::company_info()` et
 * `backend/app/marque.py::RAISON_SOCIALE`.
 *
 * ⚠️ ÉCART CONNU AU 21/09 — `RAISON_SOCIALE` vaut encore « LObsTer » côté back : les
 * devis et factures PDF sont donc émis par une personne morale que les pages légales
 * n'appellent plus ainsi. Corriger le back touche des documents comptables déjà édités
 * et sort du périmètre de cette passe ; c'est le premier point du dossier
 * `docs/legal/2026-09-21-pages-legales-a-arbitrer.md`.
 *
 * ────────────────────────────────────────────────────────────────────────────────
 * LA RÈGLE DE RÉDACTION (audit du 11/07) : un document légal écrit avant
 * l'immatriculation dit l'état RÉEL de la société, dans la phrase principale, jamais en
 * note grise, et jamais une capacité future écrite au présent. Tant qu'il n'y a pas de
 * SIREN, on n'écrit pas « immatriculée au RCS sous le numéro […] » avec un crochet à
 * remplir : on écrit « en cours d'immatriculation », qui est vrai.
 * ────────────────────────────────────────────────────────────────────────────────
 */
import { useEffect, useState } from 'react'
import axios from 'axios'

const API = import.meta.env.VITE_API_URL || ''

/**
 * Les faits TRANCHÉS — statuts du 17/08, réunion du 20/09.
 *
 * Ce bloc ne dépend d'aucune immatriculation : ces valeurs sont vraies aujourd'hui et le
 * resteront après. Elles sont ici, et nulle part ailleurs, pour que les quatre pages
 * légales ne puissent plus se contredire (les CGV annonçaient 500 € de capital quand les
 * statuts en disaient 1 000).
 */
export const SOCIETE = {
  raisonSociale: 'TerObs',
  forme: 'société par actions simplifiée',
  formeAbregee: 'SAS',
  capital: '1 000 euros',
  /** Greffe du tribunal de commerce compétent — celui du siège. */
  greffe: 'Paris',
  representant: 'Cyrille Poydessus',
  qualite: 'Président',
} as const

/**
 * ⚠️ À BASCULER À L'IMMATRICULATION — ce qui change le jour du SIREN, et rien d'autre.
 *
 * Aucune de ces valeurs n'est écrite en dur dans une page : elles arrivent du serveur.
 * Le « flip SIRET » est déjà conçu côté back comme un geste d'une minute — poser les
 * variables d'environnement du conteneur puis redémarrer, sans toucher au code
 * (`backend/app/company.py`, runbook `docs/vente/circuit-achat-mairie.md`). Les pages
 * légales suivent le même interrupteur : `/api/public/company` expose `immatriculee` et
 * `siret`, et les phrases d'identité basculent seules.
 *
 *   · SIREN  → dérivé du SIRET servi par l'API (`sirenDepuisSiret`) ;
 *   · SIRET  → variable d'environnement `LOBSTER_SIRET` côté back ;
 *   · RCS    → le greffe est déjà connu (Paris), seul le numéro manque ;
 *   · TVA    → numéro de TVA intracommunautaire FR41 131 499 741, écrit à la main dans
 *              `MentionsLegalesPage.tsx` (24/10) ; ce n'est pas un simple branchement.
 */
export const A_BASCULER_A_L_IMMATRICULATION = {
  siren: 'servi par /api/public/company (champ siret)',
  siret: "variable d'environnement LOBSTER_SIRET",
  rcs: `greffe de ${SOCIETE.greffe} déjà acté — seul le numéro manque`,
  tvaIntracommunautaire: 'non affiché à ce jour — régime de TVA non tranché',
} as const

/** Le SIREN, ce sont les neuf premiers chiffres du SIRET. */
export function sirenDepuisSiret(siret: string | null | undefined): string | null {
  const chiffres = (siret ?? '').replace(/\D/g, '')
  return chiffres.length >= 9 ? chiffres.slice(0, 9) : null
}

/**
 * La phrase d'identité de la société, dans les deux états.
 *
 * Elle ne contient JAMAIS de crochet de réservation : avant le SIREN, elle dit « en
 * cours d'immatriculation » — une phrase vraie, lisible par un client, et qui n'a pas
 * besoin d'être expliquée en bas de page.
 */
export function phraseIdentite(siren: string | null, siegeServi: string | null = null): string {
  const immatriculation = siren
    ? `immatriculée au registre du commerce et des sociétés de ${SOCIETE.greffe} sous le numéro ${siren}`
    : `en cours d'immatriculation au registre du commerce et des sociétés de ${SOCIETE.greffe}`
  const siege = siegeAffichable(siren, siegeServi)
  return (
    `${SOCIETE.raisonSociale}, ${SOCIETE.forme} ${immatriculation}, ` +
    `au capital de ${SOCIETE.capital}` +
    (siege ? `, dont le siège social est situé ${siege}` : '')
  )
}

/**
 * L'adresse du siège — ou `null` tant que la société n'est pas immatriculée.
 *
 * ⚠️ LE SIÈGE EST LE DOMICILE DE SON PRÉSIDENT, ET IL N'EST ÉCRIT NULLE PART DANS CE DÉPÔT CÔTÉ
 * SITE. C'est voulu, et il a fallu s'y reprendre à deux fois. Première version : l'adresse était
 * affichée dès aujourd'hui sur trois pages, alors qu'elle ne figurait nulle part sur le site.
 * Deuxième version : elle n'était plus AFFICHÉE avant le SIREN — mais elle restait une constante
 * de ce fichier, donc lisible par n'importe qui dans le JavaScript servi. « Pas affichée » n'est
 * pas « pas publiée » : un bundle se lit, et s'indexe.
 *
 * Elle vient donc du SERVEUR, et seulement quand la société existe : `/api/public/company` ne
 * rend le champ `adresse` que si `immatriculee` est vrai (`backend/app/company.py`). Le jour de
 * l'immatriculation, l'adresse devient publique au registre du commerce et la loi impose à
 * l'éditeur d'un site de la publier (LCEN, art. 6 III) : on pose alors `LOBSTER_ADRESSE` à côté
 * de `LOBSTER_SIRET` dans l'environnement du serveur, et les pages l'affichent — le même geste
 * d'une minute que le reste. Avant ce jour, rien ne l'exige, et publier le domicile d'une
 * personne n'est pas un geste qu'on fait par défaut, ni à sa place.
 */
export function siegeAffichable(siren: string | null, siegeServi: string | null): string | null {
  return siren && siegeServi ? siegeServi : null
}

/** Le statut juridique en une ligne, pour les fiches (mentions légales). */
export function statutJuridique(siren: string | null): string {
  return siren
    ? `${SOCIETE.forme} (${SOCIETE.formeAbregee}) au capital de ${SOCIETE.capital}`
    : `${SOCIETE.forme} (${SOCIETE.formeAbregee}) en cours d'immatriculation, au capital de ${SOCIETE.capital}`
}

/** Ce qu'on affiche en face de « SIREN » tant qu'il n'y en a pas. */
export function mentionSiren(siren: string | null): string {
  return siren ?? "En cours d'immatriculation"
}

/**
 * Le SIREN et le siège de la société, ou `null` tant qu'elle n'est pas immatriculée.
 *
 * ⚠️ LE DÉFAUT EST `null`, ET C'EST TOUT L'INTÉRÊT. Pendant le chargement, si l'API ne
 * répond pas, si le champ manque — la page affiche « en cours d'immatriculation ». Une
 * panne réseau ne peut donc pas faire dire à une page légale qu'une société existe alors
 * qu'elle n'existe pas ; elle peut seulement retarder l'annonce de l'inverse. Des deux
 * erreurs possibles, c'est la seule qui soit sans conséquence.
 */
export interface IdentiteServie { siren: string | null; siege: string | null }

export function useSociete(): IdentiteServie {
  const [identite, setIdentite] = useState<IdentiteServie>({ siren: null, siege: null })
  useEffect(() => {
    let vivant = true
    axios
      .get<{ immatriculee?: boolean; siret?: string | null; siren?: string | null; adresse?: string | null }>(`${API}/api/public/company`)
      .then((r) => {
        if (!vivant) return
        // Depuis le 23/10/2026, le serveur sert le SIREN dès l'immatriculation, avant
        // l'ouverture de la facturation (SIRET) ; l'ancien chemin reste lu en repli.
        const siren = sirenDepuisSiret(r.data?.siren) ?? (r.data?.immatriculee ? sirenDepuisSiret(r.data?.siret) : null)
        // Ceinture et bretelles : même si le serveur rendait une adresse sans SIREN, on ne
        // l'afficherait pas. La règle tient des deux côtés du fil.
        setIdentite({ siren, siege: siren ? (r.data?.adresse || null) : null })
      })
      .catch(() => {
        if (vivant) setIdentite({ siren: null, siege: null })
      })
    return () => {
      vivant = false
    }
  }, [])
  return identite
}
